import type {
  AskStreamEvent,
  SuperSource,
  GPTMessage,
  SuperFeatures,
  SuperTools,
} from '..'
import { buildUrl } from '../utils/build-url'
import { handleAskEvents } from '../ask'
import { fetchSSE } from '../utils/fetchSSE'

export type SuperFilters = {
  parentIdList?: string[]
  sourceTypeList?: SuperSource[]
  updatedAfter?: string | null
  updatedBefore?: string | null
  excludeSourceTypeList?: SuperSource[]
  excludeParentIdList?: string[]
  searchDatabaseRecords?: boolean | null
  authorEmails?: string[]
  authorName?: string
}

export type FetchSuperParams = {
  token: string
  baseUrl: string
  question: string
  context: GPTMessage[]
  instructions?: string
  citeSourceInPrompt?: boolean
  features?: Partial<SuperFeatures>
  lang?: string
  onData?: (askData: AskStreamEvent) => void
  user?: string
  isReactNative?: boolean
  signal?: AbortSignal
  batchSize?: number
  shouldStop?: () => boolean
  debug?: boolean
  filters: SuperFilters
  tools?: SuperTools[]
  threadId?: string
  clearThread?: boolean
  images?: string[]
}

const SUPER_ASK_NO_FEATURES: SuperFeatures = Object.freeze({
  useHistory: false,
  citeSourceInPrompt: false,
  useArtifactsInAnswer: false,
  followupClarification: false,
  explainSources: false,
  explainCitedSources: false,
  promptCaching: false,
})

export async function fetchSuper({
  baseUrl,
  onData = () => {
    // Do nothing by default
  },
  lang,
  debug = false,
  question,
  token,
  user,
  isReactNative,
  signal = new AbortController().signal,
  context,
  shouldStop,
  instructions,
  features,
  filters,
  threadId,
  clearThread,
  images,
}: FetchSuperParams) {
  await fetchSSE<AskStreamEvent>({
    fetchInput: buildUrl(
      baseUrl,
      '/super-stream-graph',
      debug ? { debug: 'true' } : {}
    ),
    fetchInit: {
      method: 'POST',
      signal,
      headers: new Headers({
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
      body: JSON.stringify({
        question,
        filters,
        instructions,
        lang,
        user,
        context,
        threadId,
        clearThread,
        images,
        features: {
          ...SUPER_ASK_NO_FEATURES,
          ...(features || {}),
        },
      }),
      ...(isReactNative
        ? {
            reactNative: { textStreaming: true },
          }
        : {}),
    },
    onData,
    sseEventHandler: handleAskEvents,
    shouldStop,
  })
}

export async function fetchSuperNewGraph({
  baseUrl,
  onData = () => {
    // Do nothing by default
  },
  lang,
  debug = false,
  question,
  token,
  user,
  isReactNative,
  signal = new AbortController().signal,
  context,
  shouldStop,
  instructions,
  features,
  filters,
  threadId,
  clearThread,
}: FetchSuperParams) {
  await fetchSSE<AskStreamEvent>({
    fetchInput: buildUrl(
      baseUrl,
      '/super-stream-new-graph',
      debug ? { debug: 'true' } : {}
    ),
    fetchInit: {
      method: 'POST',
      signal,
      headers: new Headers({
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
      body: JSON.stringify({
        question,
        filters,
        instructions,
        lang,
        user,
        context,
        threadId,
        clearThread,
        features: {
          ...SUPER_ASK_NO_FEATURES,
          ...(features || {}),
        },
      }),
      ...(isReactNative
        ? {
            reactNative: { textStreaming: true },
          }
        : {}),
    },
    onData,
    sseEventHandler: handleAskEvents,
    shouldStop,
  })
}
